import { useEffect, useMemo, useRef, useState } from "react";
import { fetchCatalog, fetchHomePage } from "../api/catalog";
import LatestProducts from "../components/LatestProducts";
import ProductOffer from "../components/ProductOffer";
import StoreBoot from "../components/StoreBoot";
import StoreCredit from "../components/StoreCredit";
import StorePixels from "../components/StorePixels";
import StoreFooterLinks from "../components/StoreFooterLinks";
import StoreHeader from "../components/StoreHeader";
import { applyPageSeo, currentPageUrl, publicTitle, seoFromContent } from "../lib/pageSeo";
import { hasRichMarkup, sanitizeRichText } from "../lib/richText";
import { sanitizeHtml } from "../lib/sanitizeHtml";
import { storePath as storefrontPath } from "../lib/storePaths";

function storePath(storeSlug, suffix = "") {
  return storefrontPath(suffix, storeSlug);
}

function destination(block, storeSlug) {
  if (block.target === "products") return storePath(storeSlug, "/products");
  if (block.target === "product" && block.productHandle) {
    return storePath(
      storeSlug,
      `/products/${encodeURIComponent(block.productHandle)}`
    );
  }
  if (block.target === "page" && block.pageSlug) {
    return storePath(storeSlug, `/pages/${encodeURIComponent(block.pageSlug)}`);
  }
  return block.url || "#";
}

function alignClass(align) {
  return ["left", "center", "right"].includes(align)
    ? `home-block--${align}`
    : "home-block--left";
}

export function pageColumns(content) {
  if (content?.columns) {
    return {
      left: content.columns.left || [],
      main: content.columns.main || [],
      right: content.columns.right || [],
    };
  }
  const blocks = Array.isArray(content?.blocks) ? content.blocks : [];
  return {
    left: [],
    main: blocks.length
      ? [
          {
            id: "legacy-main-section",
            name: "",
            settings: { padding: "medium", contentWidth: "contained" },
            elements: blocks,
          },
        ]
      : [],
    right: [],
  };
}

export function visibleColumns(layout) {
  if (layout === "sidebar-left") return ["left", "main"];
  if (layout === "sidebar-right") return ["main", "right"];
  if (layout === "two-sidebars") return ["left", "main", "right"];
  return ["main"];
}

function richMarkup(value) {
  const raw = String(value || "");
  if (!hasRichMarkup(raw)) return "";
  return sanitizeRichText(raw);
}

function emphasizedText(value) {
  const text = String(value || "");
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((part, index) =>
    part.startsWith("*") && part.endsWith("*") && part.length > 2 ? (
      <em key={index}>{part.slice(1, -1)}</em>
    ) : (
      part
    )
  );
}

function videoEmbed(url, { autoplay = false, controls = true } = {}) {
  const value = String(url || "").trim();
  if (!value) return null;
  const youtube = value.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/
  );
  if (youtube) {
    const params = new URLSearchParams({
      autoplay: autoplay ? "1" : "0",
      mute: autoplay ? "1" : "0",
      controls: controls ? "1" : "0",
      playsinline: "1",
      rel: "0",
      modestbranding: "1",
    });
    if (autoplay) {
      params.set("loop", "1");
      params.set("playlist", youtube[1]);
    }
    return {
      kind: "youtube",
      src: `https://www.youtube.com/embed/${youtube[1]}?${params.toString()}`,
    };
  }
  const vimeo = value.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) {
    const params = new URLSearchParams({
      autoplay: autoplay ? "1" : "0",
      muted: autoplay ? "1" : "0",
      loop: autoplay ? "1" : "0",
      controls: controls ? "1" : "0",
    });
    if (!controls) params.set("background", "1");
    return {
      kind: "vimeo",
      src: `https://player.vimeo.com/video/${vimeo[1]}?${params.toString()}`,
    };
  }
  return { kind: "file", src: value };
}

function FaqAccordion({ items }) {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="home-faq">
      {items.map((item, index) => {
        const open = openIndex === index;
        return (
          <div className={`home-faq__item${open ? " is-open" : ""}`} key={`${item.question}-${index}`}>
            <button
              type="button"
              className="home-faq__question"
              aria-expanded={open}
              onClick={() => setOpenIndex(open ? null : index)}
            >
              <span>{item.question}</span>
              <span className="home-faq__icon" aria-hidden="true">
                {open ? "−" : "+"}
              </span>
            </button>
            {open ? <div className="home-faq__answer">{item.answer}</div> : null}
          </div>
        );
      })}
    </div>
  );
}

function MediaCaption({ kicker, caption, position = "bottom-left", tone = "light" }) {
  if (!kicker && !caption) return null;
  return (
    <div
      className={`home-media-caption home-media-caption--${position} home-media-caption--${tone}`}
    >
      {kicker ? <p className="home-media-caption__kicker">{kicker}</p> : null}
      {caption ? (
        <p className="home-media-caption__text">{emphasizedText(caption)}</p>
      ) : null}
    </div>
  );
}

function ImageBlock({ block, className }) {
  const pins = Array.isArray(block.pins) ? block.pins : [];
  const markers = pins.filter((pin) => pin.style === "marker");
  const labels = pins.filter((pin) => pin.style !== "marker");
  const [active, setActive] = useState(0);
  const marker = markers[Math.min(active, Math.max(markers.length - 1, 0))];
  const image = (
    <img
      className={`home-image home-image--w-${block.width || "full"} home-image--fit-${block.objectFit || "contain"} home-image--r-${block.radius || "medium"} home-image--mh-${block.maxHeight || "none"}`}
      src={block.url}
      alt={block.alt || ""}
    />
  );
  return (
    <div className={className}>
      <div className={`home-media${markers.length ? " home-media--hotspots" : ""}`}>
        {block.linkUrl ? <a href={block.linkUrl}>{image}</a> : image}
        <MediaCaption
          kicker={block.captionKicker}
          caption={block.caption}
          position={block.captionPosition}
          tone={block.captionTone}
        />
        {(block.floats || []).map((item) => (
          <img
            key={`${item.url}-${item.x}-${item.y}`}
            className="home-float"
            src={item.url}
            alt=""
            style={{ left: `${item.x}%`, top: `${item.y}%` }}
          />
        ))}
        {labels.map((pin) => (
          <div
            key={`${pin.position || pin.x}-${pin.label}`}
            className={`home-pin${pin.x == null ? ` home-pin--${pin.position || "bottom-left"}` : ""}`}
            style={pin.x == null ? undefined : { left: `${pin.x}%`, top: `${pin.y}%` }}
          >
            {pin.label ? <strong>{pin.label}</strong> : null}
            {pin.detail ? <span>{pin.detail}</span> : null}
          </div>
        ))}
        {markers.map((pin, index) => (
          <button
            key={`${pin.label}-${pin.x}-${pin.y}`}
            type="button"
            className={`home-marker${index === active ? " is-active" : ""}`}
            style={{ left: `${pin.x ?? 50}%`, top: `${pin.y ?? 50}%` }}
            onClick={() => setActive(index)}
          >
            {pin.label || index + 1}
          </button>
        ))}
        {marker ? (
          <article className="home-note">
            <span>{String(active + 1).padStart(2, "0")}</span>
            {marker.title ? <h3>{marker.title}</h3> : null}
            {marker.detail ? <p>{marker.detail}</p> : null}
          </article>
        ) : null}
        {block.stamp?.label || block.stamp?.text ? (
          <div className="home-stamp">
            {block.stamp.label ? <span>{block.stamp.label}</span> : null}
            {block.stamp.text ? <strong>{block.stamp.text}</strong> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function HomeBlock(props) {
  const rendered = <HomeBlockBody {...props} />;
  if (!props.preview || !props.block?.id) return rendered;
  return (
    <div
      className="home-preview-anchor"
      data-block-id={props.block.id}
      data-block-type={props.block.type}
    >
      {rendered}
    </div>
  );
}

function HomeBlockBody({ block, latestProducts, storeSlug, preview = false }) {
  const mobile =
    block.mobile && typeof block.mobile === "object" ? block.mobile : {};
  const hideClass = [
    block.hidden ? " home-hide--desktop" : "",
    mobile.hidden ? " home-hide--mobile" : "",
  ].join("");
  const mobileAlignClass =
    mobile.align && mobile.align !== block.align
      ? ` home-block--mobile-${mobile.align}`
      : "";
  const mobileSizeClass =
    mobile.fontSize && mobile.fontSize !== block.fontSize
      ? ` home-type--mobile-${mobile.fontSize}`
      : "";
  const className = `home-block home-block--${block.type} ${
    block.layout === "full" ? "home-block--full" : ""
  } ${alignClass(block.align)}${mobileAlignClass}${mobileSizeClass}${hideClass}`;

  if (block.type === "eyebrow") {
    return (
      <div className={className}>
        <p className="home-eyebrow">{block.text}</p>
      </div>
    );
  }

  if (block.type === "heading") {
    const Tag = block.level === "display" ? "h1" : "h2";
    const sizeClass = ["small", "medium", "large", "xl"].includes(block.fontSize)
      ? ` home-heading--size-${block.fontSize}`
      : "";
    const headingMarkup = richMarkup(block.text);
    const headingStyle = block.color ? { color: block.color } : undefined;
    return (
      <div className={className}>
        <Tag
          className={`home-heading home-heading--${block.level || "section"}${sizeClass}`}
          style={headingStyle}
        >
          {headingMarkup ? (
            <span dangerouslySetInnerHTML={{ __html: headingMarkup }} />
          ) : (
            emphasizedText(block.text)
          )}
        </Tag>
      </div>
    );
  }

  if (block.type === "paragraph") {
    const font = ["sans", "serif", "arial", "times", "courier"].includes(
      block.fontFamily
    )
      ? block.fontFamily
      : "inherit";
    const size = ["small", "medium", "large", "xl"].includes(block.fontSize)
      ? block.fontSize
      : "medium";
    const mobileSize = ["small", "medium", "large", "xl"].includes(
      mobile.fontSize
    )
      ? mobile.fontSize
      : "";
    const paragraphMarkup = richMarkup(block.text);
    const paragraphClass = `home-paragraph home-paragraph--font-${font} home-paragraph--size-${size}${
      mobileSize ? ` home-paragraph--mobile-size-${mobileSize}` : ""
    }`;
    const paragraphStyle = block.color ? { color: block.color } : undefined;
    if (paragraphMarkup) {
      const ParagraphTag = /<p[\s>]/i.test(paragraphMarkup) ? "div" : "p";
      return (
        <div className={className}>
          <ParagraphTag
            className={paragraphClass}
            style={paragraphStyle}
            dangerouslySetInnerHTML={{ __html: paragraphMarkup }}
          />
        </div>
      );
    }
    return (
      <div className={className}>
        <p className={paragraphClass} style={paragraphStyle}>
          {block.text}
        </p>
      </div>
    );
  }

  if (block.type === "image") {
    if (preview && !String(block.url || "").trim()) {
      return (
        <div className={className}>
          <div className="home-media-placeholder">
            <span>{block.alt || "Image"}</span>
          </div>
        </div>
      );
    }
    return <ImageBlock block={block} className={className} />;
  }

  if (block.type === "video") {
    const autoplay = Boolean(block.autoplay);
    const controls = block.controls !== false;
    const embed = videoEmbed(block.url, { autoplay, controls });
    if (!embed) {
      if (!preview) return null;
      return (
        <div className={className}>
          <div className="home-media-placeholder">
            <span>{block.caption || "Video"}</span>
          </div>
        </div>
      );
    }
    return (
      <div className={className}>
        <div className="home-media home-media--video">
          {embed.kind === "file" ? (
            <video
              className={`home-video home-image--w-${block.width || "full"} home-image--fit-${block.objectFit || "contain"} home-image--r-${block.radius || "medium"} home-image--mh-${block.maxHeight || "none"}`}
              src={embed.src}
              poster={block.poster || undefined}
              autoPlay={autoplay}
              muted={autoplay}
              loop={autoplay}
              playsInline
              controls={controls}
            />
          ) : (
            <div className="home-video-frame">
              <iframe
                src={embed.src}
                title={block.alt || "Video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen={controls}
              />
            </div>
          )}
          <MediaCaption
            kicker={block.captionKicker}
            caption={block.caption}
            position={block.captionPosition}
            tone={block.captionTone}
          />
        </div>
      </div>
    );
  }

  if (block.type === "marquee") {
    const items = (block.items || []).filter(Boolean);
    if (!items.length) return null;
    const loop = [...items, ...items];
    return (
      <div className="home-marquee" aria-hidden="true">
        <div className="home-marquee__track">
          {loop.map((item, index) => (
            <span key={`${item}-${index}`}>✦ {item}</span>
          ))}
        </div>
      </div>
    );
  }

  if (block.type === "badges") {
    const items = (block.items || []).filter(Boolean);
    if (!items.length) return null;
    return (
      <div className={className}>
        <ul className="home-badges">
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (block.type === "checklist") {
    const items = (block.items || []).filter(Boolean);
    if (!items.length) return null;
    return (
      <div className={className}>
        <ul className={`home-checklist home-checklist--cols-${block.columns === 2 ? 2 : 1}${block.style === "pills" ? " home-checklist--pills" : ""}`}>
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (block.type === "bars") {
    const items = (block.items || []).filter((item) => item?.label || item?.text);
    if (!items.length) return null;
    return (
      <div className={`${className} home-bars`}>
        {items.map((item, index) => (
          <div className="home-bars__row" key={`${item.label}-${index}`}>
            <span className="home-bars__track">
              <span style={{ width: `${Math.min(100, Math.max(12, Number(item.amount) || 40))}%` }}>
                {item.label ? <strong>{item.label}</strong> : null}
                {item.text ? <em>{item.text}</em> : null}
              </span>
            </span>
            {item.note ? <small>{item.note}</small> : null}
          </div>
        ))}
      </div>
    );
  }

  if (block.type === "steps") {
    const items = (block.items || []).filter((item) => item?.title || item?.text);
    if (!items.length) return null;
    const flow = block.layout === "flow";
    return (
      <div className={`${className} home-steps${flow ? " home-steps--flow" : ""}`}>
        {items.flatMap((item, index) => {
          const article = (
            <article key={`${item.title}-${index}`}>
              {flow ? null : <span>{String(index + 1).padStart(2, "0")}</span>}
              {item.title ? <h3>{item.title}</h3> : null}
              {item.text ? <p>{item.text}</p> : null}
            </article>
          );
          if (!flow || index === 0) return [article];
          return [
            <span className="home-steps__arrow" aria-hidden="true" key={`arrow-${index}`}>→</span>,
            article,
          ];
        })}
      </div>
    );
  }

  if (block.type === "prices") {
    const items = (block.items || []).filter((item) => item?.price || item?.label);
    if (!items.length) return null;
    return (
      <div className={`${className} home-prices`}>
        {items.map((item) => (
          <article key={`${item.label}-${item.price}`}>
            {item.label ? <small>{item.label}{item.compare ? <> de <s>{item.compare}</s> por</> : null}</small> : null}
            {item.price ? <strong>{item.price}</strong> : null}
            {item.note ? <span>{item.note}</span> : null}
          </article>
        ))}
      </div>
    );
  }

  if (block.type === "rating") {
    const score = Math.min(5, Math.max(1, Number(block.score) || 5));
    return (
      <div className={`${className} home-rating`}>
        <span className="home-rating__stars" aria-label={`${score} de 5`}>
          {"★".repeat(score)}
          <span className="home-rating__star--empty">{"★".repeat(5 - score)}</span>
        </span>
        {block.label ? <strong>{block.label}</strong> : null}
        {block.note ? <small>{block.note}</small> : null}
      </div>
    );
  }

  if (block.type === "tabs") {
    return <TabsBlock block={block} latestProducts={latestProducts} storeSlug={storeSlug} className={className} preview={preview} />;
  }

  if (block.type === "card") {
    const embed = block.videoUrl ? videoEmbed(block.videoUrl) : null;
    const points = (block.points || []).filter(Boolean);
    return (
      <article className={`${className} home-card${block.layout === "split" ? " home-card--split" : ""}${block.tone === "accent" || block.tone === "surface" ? ` home-card--${block.tone}` : ""}${block.mediaRatio === "portrait" || block.mediaRatio === "square" || block.mediaRatio === "wide" ? ` home-card--ratio-${block.mediaRatio}` : ""}`}>
        {block.imageUrl ? <img src={block.imageUrl} alt="" /> : null}
        {embed?.kind === "youtube" || embed?.kind === "vimeo" ? (
          <div className="home-card__video">
            <iframe src={embed.src} title={block.title || "Video"} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        ) : null}
        <div>
          {block.kicker ? <span className="home-card__kicker">{block.kicker}</span> : null}
          {block.title ? <h3>{block.title}</h3> : null}
          {block.quote ? <p>{block.quote}</p> : null}
          {points.length ? (
            <ul className="home-card__points">
              {points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          ) : null}
          {embed?.kind === "file" && block.videoUrl ? <a href={block.videoUrl}>{block.linkLabel || block.videoUrl}</a> : null}
          {!block.videoUrl && block.linkUrl ? <a href={block.linkUrl}>{block.linkLabel || block.linkUrl}</a> : null}
        </div>
      </article>
    );
  }

  if (block.type === "stats") {
    const items = (block.items || []).filter((item) => item?.value || item?.label);
    if (!items.length) return null;
    return (
      <div className={`${className} home-stats${block.layout === "bar" ? " home-stats--bar" : ""}`}>
        {items.map((item) => (
          <div key={`${item.value}-${item.label}`}>
            <strong>{item.value}</strong>
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    );
  }

  if (block.type === "comparison") {
    const columns = block.columns || [];
    if (!columns.length) return null;
    return (
      <div className={`${className} home-comparison`} style={{ "--home-compare-cols": String(columns.length) }}>
        {columns.map((column, index) => (
          <article key={column.title || index} className={index === block.highlight ? "is-highlight" : ""}>
            <h3>{column.title}</h3>
            <ul>
              {(column.items || []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    );
  }

  if (block.type === "product_offer") {
    if (preview && !String(block.productHandle || "").trim()) {
      return (
        <div className={className}>
          <div className="home-media-placeholder">
            <span>{block.buttonLabel || "Product"}</span>
          </div>
        </div>
      );
    }
    return <ProductOffer block={block} storeSlug={storeSlug} className={className} />;
  }

  if (block.type === "faq") {
    const items = (block.items || []).filter(
      (item) => item?.question || item?.answer
    );
    if (!items.length) return null;
    return (
      <div className={className}>
        <FaqAccordion items={items} />
      </div>
    );
  }

  if (block.type === "html") {
    return (
      <div
        className={`${className} home-html`}
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(block.html || ""),
        }}
      />
    );
  }

  if (block.type === "link" || block.type === "button") {
    const inline = block.type === "button" && block.display === "inline";
    return (
      <div
        className={`${className}${inline ? " home-block--button-inline" : ""}`}
      >
        <a
          className={
            block.type === "button"
              ? `home-button home-button--${block.style || "primary"}`
              : "home-link"
          }
          href={destination(block, storeSlug)}
        >
          {block.label}
        </a>
      </div>
    );
  }

  if (block.type === "row") {
    const count = Math.min(
      6,
      Math.max(1, Number(block.columnCount) || block.columns?.length || 2)
    );
    const stackOnMobile = block.stackOnMobile !== false;
    const tracks = Array.isArray(block.columnWidths)
      ? block.columnWidths
          .slice(0, count)
          .map((part) => Math.min(6, Math.max(1, Math.round(Number(part)) || 1)))
      : [];
    return (
      <div
        className={`home-row${stackOnMobile ? " home-row--stack-mobile" : " home-row--keep-mobile"}`}
        style={{
          "--home-row-cols": String(count),
          ...(tracks.length === count
            ? { "--home-row-template": tracks.map((part) => `${part}fr`).join(" ") }
            : {}),
        }}
      >
        {(block.columns || []).map((column, index) => {
          const children = column.elements || [];
          return (
            <div
              className={`home-row__column${preview && children.length === 0 ? " home-row__column--empty" : ""}`}
              data-row-id={preview ? block.id : undefined}
              data-column-index={preview ? index : undefined}
              key={column.id || index}
            >
              {children.map((child) => (
                <HomeBlock
                  block={child}
                  latestProducts={latestProducts}
                  storeSlug={storeSlug}
                  preview={preview}
                  key={child.id}
                />
              ))}
              {preview && children.length === 0 ? (
                <div className="home-builder-slot" />
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  if (block.type === "latest_products") {
    return (
      <LatestProducts
        block={block}
        storeSlug={storeSlug}
        preview={preview}
        fallbackProducts={latestProducts}
        className={className}
      />
    );
  }

  return null;
}

function TabsBlock({ block, latestProducts, storeSlug, className, preview = false }) {
  const tabs = block.tabs || [];
  const [active, setActive] = useState(0);
  const index = Math.min(active, Math.max(0, tabs.length - 1));
  const tab = tabs[index];
  const side = block.placement === "start";
  const segments = block.appearance === "segments";
  const list = block.appearance === "list";
  if (!tabs.length) return null;
  return (
    <div className={`${className} home-tabs${side ? " home-tabs--start" : ""}${segments ? " home-tabs--segments" : ""}${list ? " home-tabs--list" : ""}`}>
      <div className="home-tabs__list" role="tablist">
        {tabs.map((item, tabIndex) => (
          <button
            key={item.id || tabIndex}
            type="button"
            role="tab"
            aria-selected={tabIndex === index}
            className={tabIndex === index ? "is-active" : ""}
            onClick={() => setActive(tabIndex)}
          >
            {list ? <span>{String(tabIndex + 1).padStart(2, "0")}</span> : null}
            {item.label}
          </button>
        ))}
      </div>
      <div
        className="home-tabs__panel"
        role="tabpanel"
        data-tabs-id={preview ? block.id : undefined}
        data-tab-index={preview ? index : undefined}
      >
        {(tab?.elements || []).map((child) => (
          <HomeBlock
            block={child}
            latestProducts={latestProducts}
            storeSlug={storeSlug}
            preview={preview}
            key={child.id}
          />
        ))}
        {preview && (tab?.elements || []).length === 0 ? (
          <div className="home-builder-slot" />
        ) : null}
      </div>
    </div>
  );
}

function inkFor(background) {
  const hex = String(background || "").trim();
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) return undefined;
  const value = match[1];
  const channel = (start) => parseInt(value.slice(start, start + 2), 16);
  const luminance = (0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4)) / 255;
  return luminance < 0.62 ? "#f6f3ee" : "#1c1917";
}

export function HomeSection({ section, latestProducts, storeSlug, preview = false }) {
  const settings = section.settings || {};
  const mobile = settings.mobile && typeof settings.mobile === "object"
    ? settings.mobile
    : {};
  const variant = settings.variant ? ` home-section--${settings.variant}` : "";
  const panel = settings.panel === true;
  const hideClass = [
    settings.hidden ? " home-hide--desktop" : "",
    mobile.hidden ? " home-hide--mobile" : "",
  ].join("");
  const padding = settings.padding || "medium";
  const contentWidth = settings.contentWidth || "contained";
  const backgroundColor = settings.backgroundColor || "transparent";
  const mobilePaddingClass =
    mobile.padding && mobile.padding !== padding
      ? ` home-section--mobile-padding-${mobile.padding}`
      : "";
  const mobileWidthClass =
    mobile.contentWidth && mobile.contentWidth !== contentWidth
      ? ` home-section--mobile-${mobile.contentWidth}`
      : "";
  const mobileBackground =
    mobile.backgroundColor && mobile.backgroundColor !== backgroundColor
      ? mobile.backgroundColor
      : "";
  const textColor = settings.textColor || inkFor(backgroundColor);
  const mobileTextColor =
    mobile.textColor && mobile.textColor !== textColor ? mobile.textColor : "";
  return (
    <section
      id={section.id}
      data-section-id={preview ? section.id : undefined}
      className={`home-section home-section--padding-${padding} home-section--${contentWidth}${variant}${panel ? " home-section--panel" : ""}${hideClass}${mobilePaddingClass}${mobileWidthClass}${mobileBackground ? " home-section--mobile-bg" : ""}${mobileTextColor ? " home-section--mobile-ink" : ""}`}
      style={{
        backgroundColor: panel ? "transparent" : backgroundColor,
        color: textColor,
        ...(mobileBackground ? { "--home-mobile-bg": mobileBackground } : {}),
        ...(mobileTextColor ? { "--home-mobile-ink": mobileTextColor } : {}),
      }}
    >
      {panel ? (
        <div className="home-section__panel" style={{ backgroundColor }}>
          {(section.elements || []).map((block) => (
            <HomeBlock
              block={block}
              latestProducts={latestProducts}
              storeSlug={storeSlug}
              preview={preview}
              key={block.id}
            />
          ))}
        </div>
      ) : (
        <>
          {(section.elements || []).map((block) => (
            <HomeBlock
              block={block}
              latestProducts={latestProducts}
              storeSlug={storeSlug}
              preview={preview}
              key={block.id}
            />
          ))}
          {preview && (section.elements || []).length === 0 ? (
            <div className="home-builder-slot" />
          ) : null}
        </>
      )}
    </section>
  );
}

const BUILDER_SOURCE = "atomicpay-builder";

function blockRect(anchor) {
  const child = anchor?.firstElementChild;
  const rect = (child || anchor)?.getBoundingClientRect?.();
  if (rect && (rect.width || rect.height)) return rect;
  return anchor.getBoundingClientRect();
}

function dropContainerFrom(point) {
  const nested = point?.closest?.("[data-row-id], [data-tabs-id]");
  if (nested) return nested;
  const section = point?.closest?.("[data-section-id]");
  if (!section) return null;
  return section.querySelector(":scope > .home-section__panel") || section;
}

function insertionIndex(blocks, y) {
  for (let index = 0; index < blocks.length; index += 1) {
    const rect = blockRect(blocks[index]);
    if (y < rect.top + rect.height / 2) return index;
  }
  return blocks.length;
}

function ensureInsertLine() {
  let line = document.getElementById("builder-insert-line");
  if (line) return line;
  line = document.createElement("div");
  line.id = "builder-insert-line";
  line.className = "home-builder-insert";
  line.hidden = true;
  document.body.appendChild(line);
  return line;
}

function hideInsertLine() {
  const line = document.getElementById("builder-insert-line");
  if (line) line.hidden = true;
}

function showInsertLine(container, blocks, index) {
  const line = ensureInsertLine();
  const host = container.getBoundingClientRect();
  let top = host.top + host.height / 2;
  if (blocks.length && index <= 0) top = blockRect(blocks[0]).top;
  else if (blocks.length && index >= blocks.length) {
    top = blockRect(blocks[blocks.length - 1]).bottom;
  } else if (blocks.length) {
    top = (blockRect(blocks[index - 1]).bottom + blockRect(blocks[index]).top) / 2;
  }
  line.hidden = false;
  line.style.top = `${top}px`;
  line.style.left = `${host.left}px`;
  line.style.width = `${Math.max(host.width, 24)}px`;
}

function describeDrop(x, y, ignoreId = "") {
  document.querySelectorAll(".is-builder-drop").forEach((node) => {
    node.classList.remove("is-builder-drop");
  });
  if (!(x >= 0) || !(y >= 0)) {
    hideInsertLine();
    return null;
  }
  const container = dropContainerFrom(document.elementFromPoint(x, y));
  if (!container) {
    hideInsertLine();
    return null;
  }
  const section = container.closest?.("[data-section-id]") || container;
  const blocks = [...container.querySelectorAll(":scope > .home-preview-anchor")].filter(
    (node) => node.getAttribute("data-block-id") !== ignoreId
  );
  const index = insertionIndex(blocks, y);
  if (!blocks.length) container.classList.add("is-builder-drop");
  showInsertLine(container, blocks, index);
  const rowId =
    container.getAttribute("data-row-id") ||
    container.getAttribute("data-tabs-id") ||
    "";
  const columnRaw = container.hasAttribute("data-column-index")
    ? container.getAttribute("data-column-index")
    : container.getAttribute("data-tab-index");
  return {
    sectionId: section.getAttribute("data-section-id") || "",
    rowId,
    columnIndex: columnRaw == null || columnRaw === "" ? null : Number(columnRaw),
    index,
  };
}

function parentOriginAllowed(origin) {
  try {
    const url = new URL(origin);
    if (url.protocol === "https:" && url.hostname === "app.atomicpay.com.br") return true;
    return (
      url.protocol === "http:" &&
      (url.hostname === "localhost" || url.hostname === "127.0.0.1")
    );
  } catch {
    return false;
  }
}

export default function HomePage({ storeSlug }) {
  const builder = useMemo(
    () => new URLSearchParams(window.location.search).get("builder") === "1",
    []
  );
  const { preview, pageId } = useMemo(() => {
    const search = new URLSearchParams(window.location.search);
    return {
      preview: search.get("builder") === "1" || search.get("preview") === "1",
      pageId: search.get("page") || "",
    };
  }, []);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [latestProducts, setLatestProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const parentOrigin = useRef("");
  const scrolledSelection = useRef("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    Promise.all([
      fetchHomePage(storeSlug, { preview, pageId }),
      fetchCatalog(storeSlug, {
        page: 1,
        limit: 6,
        preview,
        signal: controller.signal,
      }).catch((catalogError) => {
        if (catalogError.name === "AbortError") throw catalogError;
        return { products: [] };
      }),
    ])
      .then(([homePage, catalog]) => {
        setData(homePage);
        setLatestProducts(catalog?.products || []);
        try {
          const seo = seoFromContent(homePage?.page?.content, {
            storeName: homePage?.store?.name,
            fallbackImage: homePage?.store?.logoUrl || "",
          });
          applyPageSeo({
            title: publicTitle(seo, homePage?.store?.name),
            description: seo.description,
            image: seo.image,
            url: currentPageUrl(),
            index: !preview && !builder,
          });
        } catch {
          document.title = homePage?.store?.name || "Loja";
        }
      })
      .catch((requestError) => {
        if (requestError.name === "AbortError") return;
        if (builder) {
          setData((current) => current || {
            store: { name: "Sua loja", slug: storeSlug },
            page: null,
          });
          setError("");
          return;
        }
        setError(
          requestError.status === 404
            ? "Esta loja não foi encontrada."
            : "Não foi possível carregar a página inicial."
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [storeSlug, preview, pageId]);

  useEffect(() => {
    if (!builder) return undefined;
    let timer = 0;
    const onMessage = (event) => {
      const message = event.data;
      if (!message || message.source !== BUILDER_SOURCE) return;
      if (!parentOriginAllowed(event.origin)) return;
      parentOrigin.current = event.origin;
      if (message.type === "content" && message.content?.columns) {
        setDraft(message.content);
        setLoading(false);
        setError("");
      }
      if (message.type === "content" || message.type === "select") {
        setSelectedId(message.selectedId || "");
      }
      if (message.type === "hit") {
        const target = describeDrop(message.x, message.y);
        window.parent?.postMessage(
          {
            source: BUILDER_SOURCE,
            type: "drop-target",
            sectionId: target?.sectionId || "",
            rowId: target?.rowId || "",
            columnIndex: target ? target.columnIndex : null,
            index: target ? target.index : null,
          },
          parentOrigin.current || "*"
        );
      }
      if (parentOrigin.current) window.clearInterval(timer);
    };
    window.addEventListener("message", onMessage);
    const notify = () => {
      if (parentOrigin.current) return;
      window.parent?.postMessage({ source: BUILDER_SOURCE, type: "ready" }, "*");
    };
    notify();
    timer = window.setInterval(notify, 400);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("message", onMessage);
    };
  }, [builder]);

  useEffect(() => {
    if (!builder) return undefined;
    let last = 0;
    const postHeight = () => {
      const root = document.querySelector(".storefront");
      if (!root) return;
      const height = Math.ceil(root.getBoundingClientRect().height);
      if (height < 200 || Math.abs(height - last) < 2) return;
      last = height;
      window.parent?.postMessage(
        { source: BUILDER_SOURCE, type: "height", height },
        parentOrigin.current || "*"
      );
    };
    postHeight();
    const root = document.querySelector(".storefront");
    const observer = new ResizeObserver(postHeight);
    if (root) observer.observe(root);
    return () => observer.disconnect();
  }, [builder, draft]);

  useEffect(() => {
    if (!builder) return undefined;
    let drag = null;

    const clearDrag = () => {
      drag?.anchor?.classList.remove("is-builder-source");
      document.body.classList.remove("is-builder-moving");
      hideInsertLine();
      drag = null;
    };

    const onDown = (event) => {
      if (event.button !== 0) return;
      if (event.target.closest?.("input, textarea, select, .home-tabs__list")) return;
      const anchor = event.target.closest?.(".home-preview-anchor");
      const elementId = anchor?.getAttribute("data-block-id");
      if (!anchor || !elementId) return;
      drag = {
        id: elementId,
        anchor,
        x: event.clientX,
        y: event.clientY,
        pointerId: event.pointerId,
        active: false,
        target: null,
      };
    };

    const onMove = (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const distance = Math.hypot(event.clientX - drag.x, event.clientY - drag.y);
      if (!drag.active) {
        if (distance < 6) return;
        drag.active = true;
        drag.anchor.classList.add("is-builder-source");
        document.body.classList.add("is-builder-moving");
        try {
          document.documentElement.setPointerCapture?.(event.pointerId);
        } catch {
          // Pointer capture is optional; the move still tracks while the pointer stays in the preview.
        }
      }
      event.preventDefault();
      drag.target = describeDrop(event.clientX, event.clientY, drag.id);
    };

    const onUp = (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const current = drag;
      clearDrag();
      document.querySelectorAll(".is-builder-drop").forEach((node) => {
        node.classList.remove("is-builder-drop");
      });
      if (!current.active || !current.target?.sectionId || !parentOrigin.current) return;
      window.parent.postMessage(
        {
          source: BUILDER_SOURCE,
          type: "move-element",
          elementId: current.id,
          sectionId: current.target.sectionId,
          rowId: current.target.rowId || "",
          columnIndex: current.target.columnIndex,
          index: current.target.index,
        },
        parentOrigin.current
      );
    };

    const blockNativeDrag = (event) => event.preventDefault();
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
    document.addEventListener("dragstart", blockNativeDrag, true);
    return () => {
      clearDrag();
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
      document.removeEventListener("dragstart", blockNativeDrag, true);
    };
  }, [builder]);

  useEffect(() => {
    if (!builder) return undefined;
    const onClick = (event) => {
      const block = event.target.closest?.("[data-block-id]");
      const section = event.target.closest?.("[data-section-id]");
      if (event.target.closest?.("a, button, input, textarea, select, summary, form")) {
        event.preventDefault();
      }
      const id = block?.getAttribute("data-block-id") || section?.getAttribute("data-section-id");
      if (!id || !parentOrigin.current) return;
      window.parent.postMessage(
        {
          source: BUILDER_SOURCE,
          type: "select",
          id,
          kind: block ? "element" : "section",
        },
        parentOrigin.current
      );
    };
    const onContextMenu = (event) => {
      event.preventDefault();
      const block = event.target.closest?.("[data-block-id]");
      const section = event.target.closest?.("[data-section-id]");
      const id = block?.getAttribute("data-block-id") || section?.getAttribute("data-section-id");
      if (!id || !parentOrigin.current) return;
      window.parent.postMessage(
        {
          source: BUILDER_SOURCE,
          type: "menu",
          id,
          kind: block ? "element" : "section",
          x: event.clientX,
          y: event.clientY,
        },
        parentOrigin.current
      );
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("contextmenu", onContextMenu);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("contextmenu", onContextMenu);
    };
  }, [builder]);

  useEffect(() => {
    if (!builder) return undefined;
    document.querySelectorAll(".is-builder-selected").forEach((node) => {
      node.classList.remove("is-builder-selected");
    });
    if (!selectedId) return undefined;
    const anchor = document.querySelector(
      `[data-block-id="${CSS.escape(selectedId)}"], [data-section-id="${CSS.escape(selectedId)}"]`
    );
    const target = anchor?.classList.contains("home-preview-anchor")
      ? anchor.firstElementChild
      : anchor;
    target?.classList.add("is-builder-selected");
    const postBox = () => {
      if (!parentOrigin.current) return;
      if (!target) {
        window.parent.postMessage(
          { source: BUILDER_SOURCE, type: "box", id: "" },
          parentOrigin.current
        );
        return;
      }
      const rect = target.getBoundingClientRect();
      window.parent.postMessage(
        {
          source: BUILDER_SOURCE,
          type: "box",
          id: selectedId,
          kind: anchor?.hasAttribute("data-block-id") ? "element" : "section",
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        },
        parentOrigin.current
      );
    };
    postBox();
    const root = document.querySelector(".storefront");
    const observer = new ResizeObserver(postBox);
    if (root) observer.observe(root);
    if (selectedId && scrolledSelection.current !== selectedId) {
      scrolledSelection.current = selectedId;
      target?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
    return () => observer.disconnect();
  }, [builder, selectedId, draft]);

  const pageContent = builder ? draft : data?.page?.content;
  const settings = pageContent?.settings || {};
  const columns = pageColumns(pageContent);
  const layout = settings.layout || "full";
  const templateClass =
    settings.templateId === "editorial-home"
      ? " store-home--editorial"
      : settings.templateId === "catalog-home"
        ? " store-home--catalog"
        : settings.templateId === "ritual-home"
          ? " store-home--ritual"
          : "";

  useEffect(() => {
    const fonts = {
      "catalog-home": {
        id: "home-font-catalog",
        href: "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;1,9..144,500;1,9..144,600&family=Inter:wght@400;500;600;700&display=swap",
      },
      "ritual-home": {
        id: "home-font-ritual",
        href: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;1,500;1,600&family=Sora:wght@300;400;500;600;700&family=Inter:wght@400;500;600;700&display=swap",
      },
    };
    const font = fonts[settings.templateId] || (settings.headingFont === "fraunces" ? fonts["catalog-home"] : settings.headingFont === "sora" ? fonts["ritual-home"] : {
      id: "home-font-inter",
      href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
    });
    if (!font || document.getElementById(font.id)) return;
    const link = document.createElement("link");
    link.id = font.id;
    link.rel = "stylesheet";
    link.href = font.href;
    document.head.appendChild(link);
  }, [settings.templateId, settings.headingFont]);
  const pageStyle = useMemo(() => {
    const primary = settings.primaryColor || "";
    const gradientStart = settings.primaryGradientStart;
    const gradientEnd = settings.primaryGradientEnd;
    return {
      backgroundColor: settings.backgroundColor || "#ffffff",
      color: settings.textColor || "#111827",
      "--home-primary": primary || "#111827",
      "--home-link": settings.linkColor || primary || "inherit",
      "--home-button-bg":
        gradientStart && gradientEnd
          ? `linear-gradient(135deg, ${gradientStart}, ${gradientEnd})`
          : settings.buttonColor || primary || "#111827",
      "--home-button-text": settings.buttonTextColor || "#ffffff",
      "--home-muted": settings.mutedColor || "#6b7280",
      fontFamily:
        settings.bodyFont === "inter"
          ? "Inter, system-ui, sans-serif"
          : undefined,
      "--home-heading-font":
        settings.headingFont === "fraunces"
          ? 'Fraunces, "Cormorant Garamond", Georgia, serif'
          : settings.headingFont === "sora"
            ? "Sora, Inter, system-ui, sans-serif"
            : "Inter, system-ui, sans-serif",
    };
  }, [settings]);

  if (loading && !draft) return <StoreBoot />;

  return (
    <div
      className={builder ? "storefront storefront--builder" : "storefront"}
      style={{
        backgroundColor: pageStyle.backgroundColor,
        color: pageStyle.color,
      }}
    >
      <StorePixels store={data?.store} />
      <StoreHeader store={data?.store} storeSlug={storeSlug} navLinks={settings.chrome?.navLinks} cta={settings.chrome?.cta} />
      <main
        className={`store-home store-home--${settings.maxWidth || "wide"}${templateClass}`}
        style={pageStyle}
      >
        {builder && !draft && !error ? null : (builder ? !draft && error : error) ? (
          <section className="state-card" role="alert">
            <h1>Não foi possível abrir a loja</h1>
            <p>{error}</p>
          </section>
        ) : (
          <div className={`store-home__layout store-home__layout--${layout}`}>
            {visibleColumns(layout).map((column) => (
              <div className={`store-home__column store-home__column--${column}`} key={column}>
                {columns[column].map((section) => (
                  <HomeSection
                    section={section}
                    latestProducts={latestProducts}
                    storeSlug={storeSlug}
                    preview={builder}
                    key={section.id}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </main>
      <footer className="store-footer">
        {settings.chrome?.newsletter?.title ? (
          <form className="store-newsletter" action={settings.chrome.newsletter.url || undefined} method="get">
            <p>{settings.chrome.newsletter.title}</p>
            <div>
              <input
                type="email"
                name="email"
                required
                placeholder={settings.chrome.newsletter.placeholder || "Email"}
                disabled={!settings.chrome.newsletter.url}
              />
              <button type="submit" disabled={!settings.chrome.newsletter.url}>
                {settings.chrome.newsletter.button || "OK"}
              </button>
            </div>
          </form>
        ) : null}
        {settings.chrome?.footerText ? <p>{settings.chrome.footerText}</p> : null}
        <StoreFooterLinks links={settings.chrome?.footerLinks} storeSlug={storeSlug} />
        <StoreCredit />
      </footer>
    </div>
  );
}
